import React, { memo } from 'react';
import { Table, Badge } from 'react-bootstrap';
import {
  extractScheduledDate,
  formatCurrency,
  removeScheduledDate,
} from '../../helpers/order';

const couponStatusBadge = (status) => {
  if (status === 'expired')
    return (
      <Badge bg="warning" text="dark" style={{ marginLeft: 8 }}>
        Expirado
      </Badge>
    );
  if (status === 'deleted')
    return (
      <Badge bg="danger" style={{ marginLeft: 8 }}>
        Eliminado en panel
      </Badge>
    );
  return null;
};

const OrderDetailsTable = ({
  cart,
  order,
  paymentDetails,
  orderProductsAddedAfter,
  shippingMethod,
  coupons = [],
  recalculatedTotal = null,
  recalcDiscountTotal = null,
  recalcWarnings = [],
  isPreparingOrder = false,
}) => {
  const hasCoupons = Array.isArray(coupons) && coupons.length > 0;

  return (
    <div className="details-table-container">
      <Table striped bordered hover>
        <tbody>
          <tr>
            <td>
              <strong>Estatus:</strong>
            </td>
            <td>{order ? order.custom_status : ''}</td>
          </tr>
          <tr>
            <td>
              <strong>Método de Pago:</strong>
            </td>
            <td>{order ? order.payment_method : ''}</td>
          </tr>
          {!!paymentDetails && (
            <tr>
              <td>
                <strong>Origen de Pago:</strong>
              </td>
              <td>{`Tarjeta ${paymentDetails.brand} •••• ${paymentDetails.last4} Exp: ${paymentDetails.exp_month}/${paymentDetails.exp_year}`}</td>
            </tr>
          )}
          <tr>
            <td>
              <strong>Fecha solicitada de entrega:</strong>
            </td>
            <td>{order ? extractScheduledDate(order.customer_message) : ''}</td>
          </tr>
          <tr>
            <td>
              <strong>Método de entrega:</strong>
            </td>
            <td>{shippingMethod}</td>
          </tr>
          {!!order && order.subsidiary && (
            <tr>
              <td>
                <strong>Sucursal asignado:</strong>
              </td>
              <td>{order.subsidiary?.name}</td>
            </tr>
          )}
          {!!order &&
            order.staff_notes &&
            order.staff_notes.includes('Stripe Transaction ID') && (
              <tr>
                <td>
                  <strong>Confirmación de Pago:</strong>
                </td>
                <td>
                  Stripe ID:{' '}
                  {order.staff_notes.match(/Stripe Transaction ID: (\w+)/)?.[1] ||
                    'ID no encontrado'}
                </td>
              </tr>
            )}
          <tr>
            <td>
              <strong>Comentarios de la orden:</strong>
            </td>
            <td>{order ? removeScheduledDate(order.customer_message) : ''}</td>
          </tr>
          <tr>
            <td>
              <strong>Referencia de entrega:</strong>
            </td>
            <td>{order ? order.billing_address?.form_fields[0]?.value : 'Sin referencia'}</td>
          </tr>
          <tr>
            <td>
              <strong>Subtotal:</strong>
            </td>
            <td>
              {!!cart
                ? formatCurrency(
                    cart.cartTotal +
                      (orderProductsAddedAfter?.reduce(
                        (acc, item) => acc + (+item.total_inc_tax || 0),
                        0
                      ) || 0)
                  )
                : '$0.00'}
            </td>
          </tr>
          <tr>
            <td>
              <strong>Descuento:</strong>
            </td>
            <td>
              {recalcDiscountTotal !== null
                ? `-${formatCurrency(recalcDiscountTotal)}`
                : order
                  ? `-${formatCurrency(order.discount_amount)}`
                  : '-$0.00'}
            </td>
          </tr>
          {hasCoupons && (
            <tr>
              <td>
                <strong>Cupones aplicados:</strong>
              </td>
              <td>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: 0,
                    listStyle: 'none',
                  }}
                >
                  {coupons.map((c, idx) => (
                    <li
                      key={c.coupon_id ?? `legacy-${idx}`}
                      style={{ marginBottom: 4 }}
                    >
                      <span style={{ fontWeight: 600 }}>
                        {c.code || `Cupón #${c.coupon_id}`}
                      </span>
                      {c.discount_description && (
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 12,
                            color: '#6c757d',
                          }}
                        >
                          ({c.discount_description})
                        </span>
                      )}
                      {c.discount_amount !== null &&
                        c.discount_amount !== undefined && (
                          <span style={{ marginLeft: 8, color: '#a94442' }}>
                            −{formatCurrency(c.discount_amount)}
                          </span>
                        )}
                      {/* Badges y warnings solo en preparando pedido — en las
                          demás vistas, lista limpia e informativa. */}
                      {isPreparingOrder && couponStatusBadge(c.status)}
                      {isPreparingOrder && c.warning && (
                        <div
                          style={{
                            fontSize: 12,
                            color: c.status === 'deleted' ? '#a94442' : '#8a6d3b',
                          }}
                        >
                          {c.warning}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
                {isPreparingOrder &&
                  recalcWarnings &&
                  recalcWarnings.length > 0 && (
                    <div
                      style={{ marginTop: 8, fontSize: 12, color: '#a94442' }}
                    >
                      {recalcWarnings.map((w, idx) => (
                        <div key={idx}>• {w}</div>
                      ))}
                    </div>
                  )}
              </td>
            </tr>
          )}
          <tr>
            <td>
              <strong>Envío:</strong>
            </td>
            <td>{order ? formatCurrency(order.shipping_cost_inc_tax) : ''}</td>
          </tr>
          <tr>
            <td>
              <strong>Total de la orden:</strong>
            </td>
            <td>{order ? formatCurrency(order.total_inc_tax) : '$0.00'}</td>
          </tr>
          {recalculatedTotal !== null && (
            <tr>
              <td>
                <strong>Total recalculado con cupones:</strong>
              </td>
              <td style={{ fontWeight: 700 }}>
                {formatCurrency(recalculatedTotal)}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default memo(OrderDetailsTable);
